// client/src/utils/businessCardImagePipeline.js
// Image capture -> crop/rotate -> derived images for OCR + colour sampling.

const DEFAULT_MAX_OCR_EDGE = 2000;
const DEFAULT_MAX_COLOR_EDGE = 700;

const blobToObjectUrl = (blob) => URL.createObjectURL(blob);

export async function fileToObjectUrl(file) {
  return URL.createObjectURL(file);
}

export function revokeObjectUrl(url) {
  try {
    URL.revokeObjectURL(url);
  } catch {
    // ignore
  }
}

async function loadImageBitmap(srcUrl) {
  // Prefer createImageBitmap for speed on modern browsers
  if (typeof createImageBitmap === 'function') {
    const res = await fetch(srcUrl);
    const blob = await res.blob();
    return await createImageBitmap(blob);
  }

  // Fallback
  const img = new Image();
  img.decoding = 'async';
  img.crossOrigin = 'anonymous';
  await new Promise((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = srcUrl;
  });
  return img;
}

function getRadianAngle(deg) {
  return (deg * Math.PI) / 180;
}

function rotateSize(width, height, rotation) {
  const rotRad = getRadianAngle(rotation);
  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

async function canvasToBlob(canvas, { type = 'image/jpeg', quality = 0.92 } = {}) {
  return await new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error('Failed to export image'));
        resolve(blob);
      },
      type,
      quality
    );
  });
}

function downscaleToMaxEdge({ width, height }, maxEdge) {
  const longEdge = Math.max(width, height);
  if (longEdge <= maxEdge) return { width, height, scale: 1 };
  const scale = maxEdge / longEdge;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
    scale
  };
}

/**
 * Create a cropped (and rotated) image blob from an input image URL.
 * @param {string} imageSrcUrl
 * @param {{x:number,y:number,width:number,height:number}} croppedAreaPixels
 * @param {number} rotationDegrees
 * @returns {Promise<Blob>}
 */
export async function cropImageToBlob(imageSrcUrl, croppedAreaPixels, rotationDegrees = 0) {
  const image = await loadImageBitmap(imageSrcUrl);
  const imgW = image.width;
  const imgH = image.height;

  // Draw the rotated image to a "safe area" canvas first
  const rotRad = getRadianAngle(rotationDegrees);
  const { width: bW, height: bH } = rotateSize(imgW, imgH, rotationDegrees);

  const safeCanvas = document.createElement('canvas');
  safeCanvas.width = Math.round(bW);
  safeCanvas.height = Math.round(bH);
  const safeCtx = safeCanvas.getContext('2d');

  safeCtx.translate(safeCanvas.width / 2, safeCanvas.height / 2);
  safeCtx.rotate(rotRad);
  safeCtx.translate(-imgW / 2, -imgH / 2);
  safeCtx.drawImage(image, 0, 0);

  const outCanvas = document.createElement('canvas');
  outCanvas.width = Math.max(1, Math.round(croppedAreaPixels.width));
  outCanvas.height = Math.max(1, Math.round(croppedAreaPixels.height));
  const outCtx = outCanvas.getContext('2d');

  outCtx.drawImage(
    safeCanvas,
    Math.round(croppedAreaPixels.x),
    Math.round(croppedAreaPixels.y),
    Math.round(croppedAreaPixels.width),
    Math.round(croppedAreaPixels.height),
    0,
    0,
    outCanvas.width,
    outCanvas.height
  );

  return await canvasToBlob(outCanvas, { type: 'image/jpeg', quality: 0.92 });
}

/**
 * Create derived images:
 * - ocrBlob: downscaled + contrast/brightness normalized
 * - colorBlob: smaller + blurred for stable palette sampling
 */
export async function createDerivedImagesFromBlob(croppedBlob, {
  maxOcrEdge = DEFAULT_MAX_OCR_EDGE,
  maxColorEdge = DEFAULT_MAX_COLOR_EDGE
} = {}) {
  const srcUrl = blobToObjectUrl(croppedBlob);
  try {
    const image = await loadImageBitmap(srcUrl);

    // OCR image
    const ocrSize = downscaleToMaxEdge({ width: image.width, height: image.height }, maxOcrEdge);
    const ocrCanvas = document.createElement('canvas');
    ocrCanvas.width = ocrSize.width;
    ocrCanvas.height = ocrSize.height;
    const ocrCtx = ocrCanvas.getContext('2d');
    ocrCtx.imageSmoothingEnabled = true;
    ocrCtx.imageSmoothingQuality = 'high';
    ocrCtx.filter = 'contrast(1.25) brightness(1.08) saturate(0.9)';
    ocrCtx.drawImage(image, 0, 0, ocrCanvas.width, ocrCanvas.height);
    const ocrBlob = await canvasToBlob(ocrCanvas, { type: 'image/jpeg', quality: 0.92 });

    // Colour sample image
    const colorSize = downscaleToMaxEdge({ width: image.width, height: image.height }, maxColorEdge);
    const colorCanvas = document.createElement('canvas');
    colorCanvas.width = colorSize.width;
    colorCanvas.height = colorSize.height;
    const colorCtx = colorCanvas.getContext('2d');
    colorCtx.imageSmoothingEnabled = true;
    colorCtx.imageSmoothingQuality = 'high';
    colorCtx.filter = 'blur(1px) saturate(1.05) contrast(1.05)';
    colorCtx.drawImage(image, 0, 0, colorCanvas.width, colorCanvas.height);
    const colorBlob = await canvasToBlob(colorCanvas, { type: 'image/jpeg', quality: 0.9 });

    return { ocrBlob, colorBlob };
  } finally {
    revokeObjectUrl(srcUrl);
  }
}

